import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { newsApi } from "../api/newsApi";
import "./NewsCarousel.css";

export default function NewsCarousel() {
  const railRef = useRef(null);
  const timerRef = useRef(null);
  const nav = useNavigate();

  const { t, i18n } = useTranslation();

  const [news, setNews] = useState([]);

  const pick = (n, base) => {
    const lang = (i18n?.language || "az").toLowerCase();
    const suffix = lang.startsWith("en") ? "En" : lang.startsWith("ru") ? "Ru" : "Az";

    const keyPascal = `${base}${suffix}`;
    const keyCamel = `${base[0].toLowerCase() + base.slice(1)}${suffix}`;

    const fallbackPascalAz = `${base}Az`;
    const fallbackCamelAz = `${base[0].toLowerCase() + base.slice(1)}Az`;

    return (
      n?.[keyCamel] ??
      n?.[keyPascal] ??
      n?.[fallbackCamelAz] ??
      n?.[fallbackPascalAz] ??
      ""
    );
  };

  const firstN = (text, n = 30) => {
    const s = (text || "").toString().trim();

    if (!s) return "";

    return s.length > n ? s.slice(0, n) + "…" : s;
  };

  const formatDMY = (value) => {
    if (!value) return "";

    const d = new Date(value);

    if (Number.isNaN(d.getTime())) return "";

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    return `${dd}.${mm}.${yyyy}`;
  };

  const scrollByPx = (px) => {
    if (!railRef.current) return;

    railRef.current.scrollBy({
      left: px,
      behavior: "smooth",
    });
  };

  const autoNext = () => {
    const el = railRef.current;

    if (!el) return;

    const card = el.querySelector(".pcCardss");
    const step = card ? card.getBoundingClientRect().width + 16 : 320;

    const maxScroll = el.scrollWidth - el.clientWidth;

    if (el.scrollLeft >= maxScroll - 2) {
      el.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    } else {
      el.scrollBy({
        left: step,
        behavior: "smooth",
      });
    }
  };

  const pause = () => {
    clearInterval(timerRef.current);
  };

  const resume = () => {
    clearInterval(timerRef.current);

    timerRef.current = setInterval(autoNext, 3500);
  };

  useEffect(() => {
    timerRef.current = setInterval(autoNext, 3500);

    return () => {
      clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await newsApi.getAll();

        setNews(res.data || []);
      } catch (e) {
        console.error("news load error:", e);

        setNews([]);
      }
    };

    load();
  }, []);

  return (
    <section className="container pcWrap" onMouseEnter={pause} onMouseLeave={resume}>
      <div className="pcHead">
        <h2 className="pcTitle">{t("news_title")}</h2>

        <div className="pcBtns">
          <button
            type="button"
            className="pcBtn"
            onClick={() => scrollByPx(-500)}
            aria-label="Prev"
          >
            ‹
          </button>

          <button
            type="button"
            className="pcBtn"
            onClick={() => scrollByPx(500)}
            aria-label="Next"
          >
            ›
          </button>
        </div>
      </div>

      <div className="pcRail" ref={railRef}>
        {news.map((n) => {
          const id = n.id ?? n.Id;
          const title = pick(n, "Title");
          const content = pick(n, "Content");
          const hasImage = Boolean(n.hasImage ?? n.HasImage);

          const manualDate =
            n.manualInsertedDate ??
            n.ManualInsertedDate ??
            n.insertedDate ??
            n.InsertedDate;

          return (
            <article key={id} className="pcCardss">
              <div className="pcImgWrap">
                {hasImage ? (
                  <img
                    src={newsApi.getImageUrl(id) + `?t=${Date.now()}`}
                    alt={title}
                    className="pcImg"
                  />
                ) : (
                  <div className="pcImg pcNoImage">{t("news_no_image")}</div>
                )}
              </div>

              <div className="pcBody">
                <div className="pcName">{title}</div>

                <div className="pcDesc">{firstN(content, 30)}</div>

                <div className="pcDate">📅 {formatDMY(manualDate)}</div>

                <button
                  type="button"
                  className="pcDetail"
                  onClick={() => nav(`/xeber/${id}`)}
                >
                  {t("news_details_btn")}
                </button>
              </div>
            </article>
          );
        })}

        {news.length === 0 ? (
          <div style={{ padding: 12, opacity: 0.7 }}>{t("news_empty")}</div>
        ) : null}
      </div>
    </section>
  );
}